/**
 * Logística → Ingresos Extras (`/logistica/ingresos-extras`).
 *
 * Puerto de `Logistics::incomes` + `views/incomes/index.php` (que se alimentaba de
 * `API/Ingresos`) y de sus acciones `Boveda::actualizar_ingreso_adicional` y
 * `Boveda::delete_ingreso_adicional`: la tabla de ingresos adicionales del turno con
 * sus filtros (fechas y monto mínimo), la edición de la fecha, la descripción y el
 * monto, y la inhabilitación del ingreso.
 *
 * Las acciones se ofrecen al dueño del ingreso o a un administrador y sólo en turnos
 * verificados, como el panel anterior; el resto de filas muestran el candado con el
 * motivo. El **vale del ingreso** ya lo genera el propio panel (PDF de la API); el
 * envío a la impresora térmica se portará con el circuito de impresión.
 */

import {
  ArrowLeft,
  Ban,
  ChevronLeft,
  ChevronRight,
  Coins,
  Filter,
  Lock,
  Pencil,
  Printer,
  RotateCcw,
  ShieldAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import { useEditarIngreso, useEliminarIngreso, useIngresos } from '../ingresos.api';
import type { IngresoEditable, IngresoExtra, IngresosFiltros } from '../ingresos.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const POR_PAGINA = 10;

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Diálogo para editar la fecha, la descripción y el monto de un ingreso. */
function EditarIngresoDialogo({
  ingreso,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  ingreso: IngresoExtra;
  guardando: boolean;
  error: string | null;
  onGuardar: (datos: IngresoEditable) => void;
  onCerrar: () => void;
}) {
  const [datos, setDatos] = useState<IngresoEditable>({
    descripcion: ingreso.descripcion ?? '',
    cantidad: String(ingreso.cantidad),
    fecha: (ingreso.fecha ?? '').slice(0, 10),
  });

  const cantidadInvalida = datos.cantidad === '' || Number(datos.cantidad) < 0;
  const puedeGuardar =
    datos.descripcion.trim() !== '' && datos.fecha !== '' && !cantidadInvalida && !guardando;

  return (
    <Dialog
      open
      onClose={onCerrar}
      size="md"
      title="Editar ingreso adicional"
      description={`Registro #${ingreso.id} · ${ingreso.usuario || 'usuario no informado'}`}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cerrar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() => onGuardar(datos)}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error">{error}</Alert> : null}

        <Input
          label="Fecha del ingreso"
          type="date"
          value={datos.fecha}
          onChange={(evento) => setDatos({ ...datos, fecha: evento.target.value })}
        />

        <Input
          label="Monto"
          type="number"
          step="0.01"
          min={0}
          value={datos.cantidad}
          error={cantidadInvalida ? 'El monto no puede estar vacío ni ser negativo.' : undefined}
          onChange={(evento) => setDatos({ ...datos, cantidad: evento.target.value })}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="ingreso-descripcion" className="text-sm font-medium text-ink-700">
            Descripción
          </label>
          <textarea
            id="ingreso-descripcion"
            rows={4}
            maxLength={300}
            className={cn(CONTROL, 'h-auto py-2')}
            value={datos.descripcion}
            onChange={(evento) => setDatos({ ...datos, descripcion: evento.target.value })}
          />
          <p className="text-xs text-ink-400">{datos.descripcion.length} de 300 caracteres</p>
        </div>
      </div>
    </Dialog>
  );
}

export function IngresosExtrasPage() {
  useDocumentTitle('Logística · Ingresos Extras');

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [montoMin, setMontoMin] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtros, setFiltros] = useState<IngresosFiltros>({
    orden: 'id',
    direccion: 'DESC',
    perPage: POR_PAGINA,
    page: 1,
  });
  const [editando, setEditando] = useState<IngresoExtra | null>(null);
  const [aEliminar, setAEliminar] = useState<IngresoExtra | null>(null);
  const [imprimiendo, setImprimiendo] = useState<number | null>(null);
  const [errorImpresion, setErrorImpresion] = useState<string | null>(null);

  const ingresos = useIngresos(filtros);
  const guardar = useEditarIngreso();
  const eliminar = useEliminarIngreso();

  const sinPermiso = ingresos.error instanceof ApiError && ingresos.error.status === 403;
  const lista = ingresos.data?.ingresos ?? [];
  const total = ingresos.data?.total ?? 0;
  const pagina = ingresos.data?.page ?? 1;
  const paginas = ingresos.data?.paginas ?? 1;
  const desdeFila = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const hastaFila = Math.min(pagina * POR_PAGINA, total);

  /** Aplica los filtros y vuelve a la primera página. */
  function aplicar() {
    setFiltros({
      desde: desde || undefined,
      hasta: hasta || undefined,
      montoMin: montoMin.trim() || undefined,
      search: busqueda.trim() || undefined,
      orden: 'id',
      direccion: 'DESC',
      perPage: POR_PAGINA,
      page: 1,
    });
  }

  /** Abre el vale del ingreso (lo genera la propia API). */
  async function imprimirVale(ingreso: IngresoExtra) {
    setErrorImpresion(null);
    setImprimiendo(ingreso.id);

    try {
      await abrirPdfAutenticado(`/api/logistica/ingresos/${ingreso.id}/voucher`);
    } catch (error) {
      setErrorImpresion(mensajeDe(error, 'No se pudo generar el vale.'));
    } finally {
      setImprimiendo(null);
    }
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/logistica"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Logística
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Coins className="size-6 text-brand-500" aria-hidden="true" />
          Ingresos Extras
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Ingresos adicionales registrados en los turnos, con su edición y su inhabilitación.
        </p>
      </div>

      {ingresos.data ? <Badge variant="outline">{formatNumber(total)} registros</Badge> : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            {mensajeDe(ingresos.error, 'Esta pantalla es del grupo logistics.')}
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="Acciones en curso de migración">
        La <strong>edición</strong> y la <strong>inhabilitación</strong> ya están disponibles para el
        dueño del ingreso (o un administrador) en turnos verificados. El <strong>vale del
        ingreso</strong> ya lo genera el propio panel; el envío a la impresora térmica se portará con
        el circuito de impresión.
      </Alert>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="ie-desde" className="text-sm font-medium text-ink-700">
                Fecha inicio
              </label>
              <CampoFecha
                id="ie-desde"
               
                className={cn(CONTROL, 'w-40')}
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="ie-hasta" className="text-sm font-medium text-ink-700">
                Fecha fin
              </label>
              <CampoFecha
                id="ie-hasta"
               
                className={cn(CONTROL, 'w-40')}
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="ie-monto" className="text-sm font-medium text-ink-700">
                Monto mínimo
              </label>
              <input
                id="ie-monto"
                type="number"
                step="0.01"
                min={0}
                className={cn(CONTROL, 'w-32')}
                placeholder="0.00"
                value={montoMin}
                onChange={(evento) => setMontoMin(evento.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="ie-buscar" className="text-sm font-medium text-ink-700">
                Búsqueda libre
              </label>
              <input
                id="ie-buscar"
                className={cn(CONTROL, 'w-48')}
                placeholder="Descripción, usuario…"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
              />
            </div>

            <Button
              size="sm"
              leftIcon={<Filter className="size-3.5" aria-hidden="true" />}
              onClick={aplicar}
            >
              Filtrar
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
              onClick={() => {
                setDesde('');
                setHasta('');
                setMontoMin('');
                setBusqueda('');
                setFiltros({ orden: 'id', direccion: 'DESC', perPage: POR_PAGINA, page: 1 });
              }}
            >
              Limpiar
            </Button>
          </div>

          {guardar.error || eliminar.error ? (
            <Alert variant="error" title="No se pudo completar la acción">
              {mensajeDe(guardar.error ?? eliminar.error, 'La API no respondió.')}
            </Alert>
          ) : null}

          {errorImpresion ? (
            <Alert variant="error" title="No se pudo generar el vale">
              {errorImpresion}
            </Alert>
          ) : null}

          {ingresos.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : ingresos.error ? (
            <Alert variant="error" title="No se pudieron leer los ingresos">
              {mensajeDe(ingresos.error, 'La API no respondió.')}
            </Alert>
          ) : lista.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              No hay ingresos adicionales con esos filtros.
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">#</th>
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Personal</th>
                    <th className="py-2">Ingreso</th>
                    <th className="py-2 text-right">Cantidad</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((ingreso) => (
                    <tr key={ingreso.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3 text-ink-500">{ingreso.id}</td>
                      <td className="py-3 text-ink-700">{ingreso.fecha ?? '—'}</td>
                      <td className="py-3">
                        <span className="block font-semibold text-ink-900">
                          {ingreso.usuario || '—'}
                        </span>
                        <span className="block text-[11px] text-ink-400">
                          {ingreso.turno_id ? `Turno #${ingreso.turno_id}` : 'Sin turno'}
                        </span>
                      </td>
                      <td className="max-w-[320px] py-3 text-ink-600">
                        {ingreso.descripcion || <span className="text-ink-300">—</span>}
                      </td>
                      <td className="py-3 text-right font-semibold text-ink-900">
                        {formatCurrency(ingreso.cantidad)}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Imprimir el vale del ingreso"
                            aria-label={`Imprimir el vale del ingreso ${ingreso.id}`}
                            loading={imprimiendo === ingreso.id}
                            onClick={() => void imprimirVale(ingreso)}
                          >
                            <Printer className="size-4 text-ink-600" aria-hidden="true" />
                          </Button>

                          {ingreso.puede_editar ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Editar ingreso"
                              aria-label={`Editar el ingreso ${ingreso.id}`}
                              onClick={() => {
                                guardar.reset();
                                setEditando(ingreso);
                              }}
                            >
                              <Pencil className="size-4 text-amber-600" aria-hidden="true" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled
                              title={
                                Number(ingreso.estado_verificacion) === 1
                                  ? 'Editar · sólo el usuario que registró el ingreso (o un administrador)'
                                  : 'Editar · sólo en turnos verificados'
                              }
                            >
                              <Lock className="size-4 text-ink-300" aria-hidden="true" />
                            </Button>
                          )}

                          {ingreso.puede_eliminar ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Eliminar ingreso"
                              aria-label={`Eliminar el ingreso ${ingreso.id}`}
                              onClick={() => {
                                eliminar.reset();
                                setAEliminar(ingreso);
                              }}
                            >
                              <Ban className="size-4 text-red-600" aria-hidden="true" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled
                              title={
                                Number(ingreso.estado_verificacion) === 1
                                  ? 'Eliminar · sólo el usuario que registró el ingreso (o un administrador)'
                                  : 'Eliminar · sólo en turnos verificados'
                              }
                            >
                              <Lock className="size-4 text-ink-300" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-ink-400">
                Mostrando {formatNumber(desdeFila)}–{formatNumber(hastaFila)} de{' '}
                {formatNumber(total)} registros · página {pagina} de {formatNumber(paginas)}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="size-3.5" aria-hidden="true" />}
                  disabled={pagina <= 1}
                  onClick={() => setFiltros({ ...filtros, page: Math.max(1, pagina - 1) })}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  rightIcon={<ChevronRight className="size-3.5" aria-hidden="true" />}
                  disabled={pagina >= paginas}
                  onClick={() => setFiltros({ ...filtros, page: pagina + 1 })}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {editando ? (
        <EditarIngresoDialogo
          ingreso={editando}
          guardando={guardar.isPending}
          error={guardar.error ? mensajeDe(guardar.error, 'No se pudo guardar el ingreso.') : null}
          onGuardar={(datos) =>
            guardar.mutate(
              { id: editando.id, ingreso: datos },
              { onSuccess: () => setEditando(null) },
            )
          }
          onCerrar={() => {
            guardar.reset();
            setEditando(null);
          }}
        />
      ) : null}

      {aEliminar ? (
        <ConfirmDialog
          open
          onClose={() => setAEliminar(null)}
          onConfirm={() => eliminar.mutate(aEliminar.id, { onSuccess: () => setAEliminar(null) })}
          title="¿Eliminar el ingreso adicional?"
          description="El ingreso quedará inhabilitado y dejará de sumar en el turno en el que se registró."
          confirmLabel="Sí, eliminar"
          loading={eliminar.isPending}
        />
      ) : null}
    </div>
  );
}
